import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { makeRequest } from '../util/AxiosInstance';
import { AppMeta, appStore, userStore } from '../zustand'; // Assuming userStore holds user info
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Button,
    Paper,
    Skeleton,
    TablePagination,
    Grid,
    IconButton,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Notify } from 'notiflix';
import { ArrowRight } from 'iconsax-react';

const ROWS_PER_PAGE = 5;

const fetchCancellationProjects = async ({ queryKey }) => {
    const [_key, page, userId] = queryKey;
    const status = AppMeta.getState().projectStatus.CUSTOMER_APPROVE_CANCELLATION;

    if (!userId) {
        // Handle case where user ID is not available yet
        console.warn("User ID not available for fetching projects.");
        return { data: [], total: 0 };
    }

    const params = new URLSearchParams({
        status: status,
        page: page + 1, // API is 1-based index
        perPage: ROWS_PER_PAGE,
        user_id: userId, // Assuming the API needs user_id for filtering/authorization
    });

    try {
        const response = await makeRequest('GET', `/projects?${params.toString()}`);
        if (response.data?.status) {
            return {
                data: response.data.data.data || [],
                total: response.data.data.total || 0,
            };
        } else {
            throw new Error(response.data?.message || 'Failed to fetch projects');
        }
    } catch (error) {
        Notify.failure(error.message || 'Could not fetch projects requiring cancellation approval.');
        console.error("Error fetching cancellation projects:", error);
        return { data: [], total: 0 }; // Return empty on error
    }
};

const ProjectCancellationApprovalList = () => {
    const [page, setPage] = useState(0); // 0-based index for MUI TablePagination
    const userId = userStore((state) => state.user?.id);
    const mode = appStore((state) => state.mode);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['cancellationProjects', page, userId],
        queryFn: fetchCancellationProjects,
        keepPreviousData: true,
        enabled: !!userId, // Only run query if userId is available
    });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const projects = data?.data ?? [];
    const totalProjects = data?.total ?? 0;

    const renderSkeleton = () => {
        return Array.from({ length: ROWS_PER_PAGE }).map((_, index) => (
            <Paper
                key={index}
                component="li"
                elevation={2}
                sx={{ mb: 2, p: 2, borderRadius: 2 }}
            >
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item xs={8}>
                        <Skeleton variant="text" width="70%" />
                    </Grid>
                    <Grid item xs={4} textAlign="right">
                        <Skeleton variant="rectangular" width={100} height={36} />
                    </Grid>
                </Grid>
            </Paper>
        ));
    };

    if (projects.length === 0) {
        return <></>
    }
    return (
        <Box className="project-list-wrapper" mb={2}>
            <Typography variant="h6" color={'error'}>
                Cancellations
            </Typography>
            <Typography variant="body2" color={'gray'} gutterBottom>
                Projects Awaiting Cancellation Approval
            </Typography>
            {isLoading ? (
                <List sx={{ padding: 0 }}>{renderSkeleton()}</List>
            ) : isError ? (
                <Typography color="error">Failed to load projects.</Typography>
            ) : projects.length === 0 ? (
                <Typography>No projects require cancellation approval.</Typography>
            ) : (
                <List sx={{ padding: 0 }}>
                    {projects.map((project) => (
                        <Paper
                            className='project-c-list-item'
                            key={project.id}
                            component="li"
                            elevation={1}
                            sx={{ mb: 2, p: 1, px: 2, borderRadius: 2 }}
                        >
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Grid item xs={8}>
                                    <ListItemText
                                        primary={project.title}
                                        primaryTypographyProps={{ fontWeight: 500 }}
                                    />
                                </Grid>
                                <Grid item xs={4} textAlign="right">
                                    <IconButton
                                        variant="outlined"
                                        size="small"
                                        component={RouterLink}
                                        to={`${mode !== 'admin' ? '' : ''}project/${project.id}`} // Adjust link as needed
                                    >
                                        <ArrowRight size={18} />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                </List>
            )}

            {totalProjects > ROWS_PER_PAGE && (
                <TablePagination
                    component="div"
                    count={totalProjects}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={ROWS_PER_PAGE}
                    rowsPerPageOptions={[ROWS_PER_PAGE]} // Hide rows per page selection
                />
            )}
        </Box>
    );
};

export default ProjectCancellationApprovalList;
